import { FC, useEffect, useRef, useState } from 'react'
import ValidatorWrapper, { rules, ValidatorField } from '@coxy/react-validator'
import cn from 'classnames'

import { constants } from 'config/constants'
import validatorRules from 'libs/validator-rules'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setFallbackEmail } from 'store/fetchers/users'
import { fetchUser, selectedUser } from 'store/reducers/users'
import {
  EMessageTypes,
  getVerifiedToken,
  resendVerificationCode,
  sendVerificationCodeWithoutCaptcha,
} from 'store/fetchers/utils'
import { InputEmail } from 'components/app/input-email'
import InputCode from 'components/app/input-code'
import ButtonBlue from 'components/ui/button/_color/_blue'
import CellHeading from 'components/ui/cell/__heading'
import { GoogleCheck2faCode } from 'components/ui/google-check-2fa-code'
import Popup from 'components/ui/popup'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

export const AddBackupEmailPopup: FC<{
  closePopup: () => void
}> = ({ closePopup }) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const validator = useRef<ValidatorWrapper>()
  const emailVerifiedToken = useRef('')
  const emailVerificationCode = useRef('')
  const newEmailVerificationCode = useRef('')
  const newEmailVerifiedToken = useRef('')
  const isActive2faRef = useRef(false)
  const [captcha, setCaptcha] = useState<string>('')

  const [isLoading, _setIsLoading] = useState(false)
  const [email, _setEmail] = useState('')

  const isLoadingRef = useRef(isLoading)
  const emailRef = useRef(email)

  const user = useAppSelector(selectedUser)

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [isVisibleErrors, setIsVisibleErrors] = useState(false)
  const [isEmailValid, setIsEmailValid] = useState<boolean>(null)
  const [emailCode, setEmailCode] = useState('')
  const [newEmailCode, setNewEmailCode] = useState('')
  const [isEmailCodeValid, setIsEmailCodeValid] = useState<boolean>(null)
  const [isNewEmailCodeValid, setIsNewEmailCodeValid] = useState<boolean>(null)
  const [isValid2faCode, setIsValid2faCode] = useState(null)
  const [newEmail, setNewEmail] = useState<string>('')
  const [isEmailBtnDisabled, setIsEmailBtnDisabled] = useState(false)
  const [isNewEmailBtnDisabled, setIsNewEmailBtnDisabled] = useState(false)
  const [emailCodeErrorCode, setEmailCodeErrorCode] = useState<string>(null)
  const [newEmailCodeErrorCode, setNewEmailCodeErrorCode] = useState<string>(null)
  const [gaCodeErrorCode, setGaCodeErrorCode] = useState<string>(null)
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>(null)

  const setIsLoading = (value: boolean): void => {
    isLoadingRef.current = value
    _setIsLoading(value)
  }

  const setEmail = (value: string): void => {
    emailRef.current = value
    _setEmail(value)
  }

  const verificationCode = emailVerificationCode.current

  const resendEmailCode = async (captcha: string): Promise<void> => {
    const token = await resendVerificationCode({
      verification_token: verificationCode,
      captcha: captcha,
    })

    emailVerificationCode.current = token
  }

  const validateEmail = async (): Promise<boolean> => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    if (!newEmail) {
      setIsEmailValid(false)
      setIsVisibleErrors(true)
      return false
    }

    if (!emailRegex.test(newEmail)) {
      setIsEmailValid(false)
      setIsVisibleErrors(true)
      setEmailErrorMessage(t('incorrect_email_error'))
      return false
    }

    setIsLoading(true)
    setIsVisibleErrors(false)
    setIsEmailValid(true)

    const { tokens, isActive2fa, error } = await sendVerificationCodeWithoutCaptcha({
      ...{ email: user.email, new_email: newEmail },
      type: EMessageTypes.SET_FALLBACK_EMAIL,
    })
    setCaptcha(captcha)
    isActive2faRef.current = isActive2fa
    emailVerificationCode.current = tokens[0]

    const result = !!emailVerificationCode.current

    if (result) {
      setStep(2)
    }

    setIsVisibleErrors(!result)
    setIsEmailValid(result)
    setIsLoading(false)

    if (error) {
      setEmailErrorMessage(error?.includes('limit') ? t('rate_limit_exceeded_retry_later') : t('incorrect_email'))
    }

    return result
  }

  const validateEmailCode = async () => {
    if (emailCode?.length !== constants.codeLength) {
      setIsEmailCodeValid(false)
      setIsVisibleErrors(true)
      return
    }

    try {
      const res = await getVerifiedToken({
        code: emailCode,
        verification_token: emailVerificationCode.current,
      })

      if (res.result) {
        emailVerifiedToken.current = res.response
        setIsVisibleErrors(!emailVerifiedToken.current)
        setIsEmailCodeValid(!!emailVerifiedToken.current)
      } else {
        setIsVisibleErrors(true)
        setIsEmailCodeValid(false)
        setEmailCodeErrorCode(res.message)
      }
    } catch (err) {
      setIsEmailCodeValid(false)
      setIsVisibleErrors(true)
      setIsEmailBtnDisabled(true)

      const msg = err?.response?.message || err?.response?.error || ''

      if (err.response && msg.includes('limit')) {
        setEmailCodeErrorCode(t('rate_limit_exceeded_retry_later'))
      } else {
        setEmailCodeErrorCode('INVALID_REQUEST_PARAMS')
      }
      setIsLoading(false)
    }
  }

  const validateCodes = async (): Promise<void> => {
    if (isLoadingRef.current) {
      return
    }

    setIsLoading(true)

    await Promise.all([validateEmailCode()])

    setIsLoading(false)
  }

  const validateNewCodes = async (): Promise<void> => {
    if (isLoadingRef.current) {
      return
    }

    setIsLoading(true)

    await Promise.all([validateNewEmailCode()])

    setIsLoading(false)
  }

  const validateNewEmailCode = async () => {
    if (newEmailCode?.length !== constants.codeLength) {
      setIsNewEmailCodeValid(false)
      setIsVisibleErrors(true)
      return
    }

    try {
      const res = await getVerifiedToken({
        code: newEmailCode,
        verification_token: newEmailVerificationCode.current,
      })
      if (res.result) {
        newEmailVerifiedToken.current = res.response
        setIsVisibleErrors(!newEmailVerifiedToken.current)
        setIsNewEmailCodeValid(!!newEmailVerifiedToken.current)
      } else {
        setIsVisibleErrors(true)
        setIsNewEmailCodeValid(false)
        setNewEmailCodeErrorCode(res.message)
      }
    } catch (err) {
      setIsNewEmailCodeValid(false)
      setIsVisibleErrors(true)
      setIsNewEmailBtnDisabled(true)

      const msg = err?.response?.message || err?.response?.error || ''

      if (err.response && msg.includes('limit')) {
        setNewEmailCodeErrorCode(t('rate_limit_exceeded_retry_later'))
      } else {
        setNewEmailCodeErrorCode('INVALID_REQUEST_PARAMS')
      }
      setIsLoading(false)
    }
  }

  const confirmUpdate = async (): Promise<void> => {
    if (!isEmailCodeValid) {
      return
    }

    if (isActive2faRef.current) {
      validateNewEmail()
      setStep(3)
    }
  }

  const onCode2faSubmit = async (code2fa) => {
    setIsLoading(true)
    try {
      const { res, error } = await setFallbackEmail({
        verification_token: [emailVerifiedToken.current, newEmailVerifiedToken.current],
        new_email: newEmail,
        code_2fa: code2fa,
      })

      if (res) {
        await dispatch(fetchUser())
        setGaCodeErrorCode(null)
        setIsValid2faCode(true)
        setIsVisibleErrors(false)
        closePopup()
      } else {
        setGaCodeErrorCode(error?.includes('limit') ? t('rate_limit_exceeded_retry_later') : 'INVALID_REQUEST_PARAMS')
        setIsValid2faCode(false)
        setIsVisibleErrors(true)
      }
    } catch {
      setGaCodeErrorCode('INVALID_REQUEST_PARAMS')
      setIsValid2faCode(false)
      setIsVisibleErrors(true)
    } finally {
      setIsLoading(false)
    }
  }

  const onChangeEmail = async () => {
    if (!isNewEmailCodeValid) {
      return
    }

    if (isActive2faRef.current) {
      setStep(4)
    }
  }

  const handleChange2FAValue = (value: string) => {
    if (value.length !== 6) {
      setIsVisibleErrors(false)
      setGaCodeErrorCode(null)
    }
  }

  const validateNewEmail = async () => {
    if (!newEmail) {
      setIsEmailValid(false)
      setIsVisibleErrors(true)
      return false
    }

    setIsLoading(true)
    setIsVisibleErrors(false)

    const { tokens, isActive2fa } = await sendVerificationCodeWithoutCaptcha({
      ...{ email: newEmail, newEmail: newEmail },
      type: EMessageTypes.SET_FALLBACK_EMAIL_NEW_EMAIL,
    })
    setCaptcha(captcha)
    isActive2faRef.current = isActive2fa
    newEmailVerificationCode.current = tokens[0]

    const result = !!newEmailVerificationCode.current

    setIsVisibleErrors(!result)
    setIsEmailValid(result)
    setIsLoading(false)

    return result
  }

  useEffect(() => {
    setIsEmailCodeValid(null)
    setIsEmailBtnDisabled(false)
    setIsVisibleErrors(false)
    setEmailCodeErrorCode(null)

    if (emailCode?.length !== constants.codeLength || isLoadingRef.current) {
      return
    }

    async function runValidateEmailCode() {
      setIsLoading(true)
      await validateEmailCode()
      setIsLoading(false)
    }

    void runValidateEmailCode()
  }, [emailCode])

  useEffect(() => {
    setIsNewEmailCodeValid(null)
    setIsNewEmailBtnDisabled(false)
    setIsVisibleErrors(false)
    setNewEmailCodeErrorCode(null)

    if (emailCode?.length !== constants.codeLength || isLoadingRef.current) {
      return
    }

    async function runValidateEmailCode() {
      setIsLoading(true)
      await validateNewEmailCode()
      setIsLoading(false)
    }

    void runValidateEmailCode()
  }, [newEmailCode])

  useEffect(() => {
    if (user) {
      setEmail(user.email)
    }
  }, [user])

  return (
    <ValidatorWrapper ref={validator}>
      <Popup
        className={styles.popup}
        crossIconBtnStyle={styles.crossIcon}
        onClose={closePopup}
        heading={t('set_backup_email')}
        subtitle={t('add_verification')}
        titleClassName={styles.popup__title}
        subtitleClassName={styles.popup__subtitle}
      >
        {step === 1 && (
          <>
            <div className={styles.phoneEmail}>
              <div className={styles.fieldWrapper}>
                <ValidatorField value={email} rules={validatorRules.email}>
                  <CellHeading className={styles.fieldHead}>{t('email_set_backup')}</CellHeading>
                  <InputEmail
                    onChange={(value) => {
                      setIsVisibleErrors(false)
                      setEmailErrorMessage(null)
                      setNewEmail(value)
                    }}
                    isValid={isEmailValid}
                    isVisibleError={isVisibleErrors}
                    value={newEmail}
                    errorMessage={emailErrorMessage}
                  />
                </ValidatorField>
              </div>
            </div>
            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <ButtonBlue onClick={validateEmail} className={styles.verifyButton} isLoading={isLoading}>{t('verify')}</ButtonBlue>
            </div>
          </>
        )}

        {step === 2 && (
          <div className={styles.codes}>
            <div className={styles.codeRequestWrapper}>
              <div className={styles.fieldWrapper}>
                <ValidatorField value={emailCode} rules={rules.bool} />
                <CellHeading className={styles.input__label}>{t('enter_code_sent_to')} {user.email}</CellHeading>
                <InputCode
                  onEnter={validateCodes}
                  isValid={isEmailCodeValid}
                  value={emailCode}
                  onChange={(code: string) => {
                    setEmailCode(code)
                    setIsVisibleErrors(false)
                    setEmailCodeErrorCode(null)
                    setIsEmailBtnDisabled(false)
                  }}
                  onResendCode={resendEmailCode}
                  isVisibleError={isVisibleErrors}
                  disabled={isEmailCodeValid === true}
                  errorCode={emailCodeErrorCode}
                />
              </div>
            </div>

            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <ButtonBlue disabled={isEmailBtnDisabled} className={styles.verifyButton} onClick={confirmUpdate}>{t('confirm')}</ButtonBlue>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={styles.codes}>
            <div className={styles.codeRequestWrapper}>
              <div className={styles.fieldWrapper}>
                <ValidatorField value={newEmailCode} rules={rules.bool} />
                <CellHeading className={styles.input__label}>{t('enter_code_sent_to')} {newEmail}</CellHeading>
                <InputCode
                  onEnter={validateNewCodes}
                  isValid={isNewEmailCodeValid}
                  value={newEmailCode}
                  onChange={(code: string) => {
                    setNewEmailCode(code)
                    setIsVisibleErrors(false)
                    setNewEmailCodeErrorCode(null)
                    setIsNewEmailBtnDisabled(false)
                  }}
                  onResendCode={resendEmailCode}
                  isVisibleError={isVisibleErrors}
                  disabled={isNewEmailCodeValid === true}
                  errorCode={newEmailCodeErrorCode}
                />
              </div>
            </div>

            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <ButtonBlue disabled={isNewEmailBtnDisabled} className={styles.verifyButton} onClick={onChangeEmail}>{t('confirm')}</ButtonBlue>
            </div>
          </div>
        )}

        {step === 4 && (
          <GoogleCheck2faCode
            classNameInputCode={styles.inputCode2fa}
            onSubmit={onCode2faSubmit}
            isCodeValid={isValid2faCode}
            isLoading={isLoading}
            setIsValidCode={(v) => setIsValid2faCode(v)}
            errorCode={gaCodeErrorCode}
            isVisibleError={isVisibleErrors}
            onChangeValue={handleChange2FAValue}
            btnClassName={styles.verifyButton}
          />
        )}
      </Popup>
    </ValidatorWrapper>
  )
}
